import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {merge, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, pairwise, take} from 'rxjs/operators';
import {ChatDataProviderService} from '../../features/chat/chat.data-provider.service';
import {CurrentUserViewModel} from '../models/current-user.view-model';
import {EventSourceService} from './event-source.service';
import {NotificationDataProviderService} from './notification.data-provider.service';
import {TokenDataProviderService} from './token-data-provider.service';
import {AppForegroundService} from './app-foreground.service';
import {NetworkStatusService} from './network-status.service';

/** Owns the user channels independently of the currently rendered navigation. */
@Injectable({providedIn: 'root'})
export class UserRealtimeService implements OnDestroy {
  private started = false;
  private userId: string | null = null;
  private subscriptions = new Subscription();
  private userSubscriptions = new Subscription();

  constructor(
    private readonly tokens: TokenDataProviderService,
    private readonly chat: ChatDataProviderService,
    private readonly notifications: NotificationDataProviderService,
    private readonly events: EventSourceService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly foreground: AppForegroundService,
    private readonly network: NetworkStatusService,
  ) {}

  public initialize(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) return;
    this.started = true;
    this.foreground.initialize();
    void this.network.init().catch(() => {});
    this.subscriptions = new Subscription();
    this.subscriptions.add(this.tokens.sessionChanged$.subscribe(() => this.reset()));
    this.subscriptions.add(this.tokens.browserReady$.pipe(take(1)).subscribe(() => {
      this.subscriptions.add(this.tokens.user$.subscribe(user => this.connect(user)));
    }));
  }

  private connect(user: CurrentUserViewModel): void {
    const userId = user?.id && this.tokens.isLoggedIn() ? String(user.id) : null;
    if (this.userId === userId) return;
    if (this.userId !== null) this.reset();
    if (!userId) return;
    this.userId = userId;

    this.userSubscriptions.add(merge(
      this.events.reconnected$.pipe(filter(topics => topics.some(topic => topic === `/users/${userId}/conversations`
        || /^\/conversations\/[^/]+\/messages$/.test(topic)))),
      this.foreground.foreground$.pipe(distinctUntilChanged(), pairwise(), filter(([before, after]) => !before && after)),
      this.network.status$.pipe(map(state => state.connected), distinctUntilChanged(), pairwise(), filter(([before, after]) => !before && after)),
    ).subscribe(() => this.chat.recoverMessages()));

    this.userSubscriptions.add(this.chat.listenUnreadCount(userId).subscribe(event => {
      if (typeof event?.payload?.count === 'number') this.chat.setUnreadCount(event.payload.count);
    }));
    this.userSubscriptions.add(this.chat.listenConversationList(userId).subscribe(event => {
      this.chat.applyConversationEvent(event);
    }));
    this.userSubscriptions.add(this.notifications.setEventSource(user).subscribe());
    // Errors must not tear down session observation or prevent later explicit refreshes.
    this.userSubscriptions.add(this.chat.getUnreadCount().subscribe({error: () => {}}));
    this.userSubscriptions.add(this.notifications.getUnreadCount().subscribe({error: () => {}}));
  }

  private reset(): void {
    this.userId = null;
    this.userSubscriptions.unsubscribe();
    this.userSubscriptions = new Subscription();
    this.events.closeAll();
    this.chat.resetUnreadCount();
    this.notifications.resetForSession();
  }

  public destroy(): void {
    if (!this.started) return;
    this.started = false;
    this.subscriptions.unsubscribe();
    this.reset();
  }

  public ngOnDestroy(): void { this.destroy(); }
}
